/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import React, { useState } from "react";
import Paper from "@mui/material/Paper";
import deathCheckinStyle from "./deathCheckinStyle";
import * as loginActions from "../../../../store/login/actions";
import { useLocation, useNavigate } from "react-router";
import { getLpByLocation } from "../../../../services/landingPagesList";
import Typography from "@mui/material/Typography";
import { FormProvider } from "../../../../provider/Form/FormProvider";
import FamilyLawCheckinForm from "../Alimony/FamilyLawCheckinForm";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createResource, queryKeys } from "../../../../services/reactQuery/reactQueryService";
import {Box} from "@mui/material";
import { getPartnerNameByIri } from "../../../../constants/partnerIris";
import type { PartnerIri } from "../../../../constants/partnerIris";

type DeathCheckinProps = {
  idPrefix: string;
  color?: "default" | "grey";
  checkinHeading?: string;
  target?: string;
  partner: PartnerIri;
};

export default function DeathCheckin({
  color = "default",
  target = "/servicewelt",
  idPrefix,
  checkinHeading = "Persönliches Dokument erstellen und Erstberatung vereinbaren",
  partner,
}: DeathCheckinProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [emailErrorText, setEmailErrorText] = useState("");
  const initialValues = {
    telephone: "",
    email: "",
    gender: "",
    familyName: "",
    givenName: "",
  };
  const queryClient = useQueryClient();
  const createMutation = useMutation({
    mutationFn: createResource,
    onSuccess: (data, variables) => queryClient.setQueryData(queryKeys.item(variables.uri, data.id), data),
  });
  const handleSubmit = async ({ values }: { values: Record<string, any> }) => {
    setIsLoading(true);

    const { from } = location.state || { from: { pathname: target } };
    const emailCheck = await loginActions.checkEmail(values.email.toLowerCase());
    if (emailCheck.hasError) {
      setIsLoading(false);
      setEmailErrorText(emailCheck.errorText);
      return;
    }

    const landingPage = getLpByLocation(location.pathname) || { keyword: location.pathname };
    const partnerName = getPartnerNameByIri(partner);
    const data = {
      email: values.email.toLowerCase(),
      gclid: localStorage.getItem("gclid"),
      telephone: values.telephone,
      person: {
        gender: values.gender,
        givenName: values.givenName,
        familyName: values.familyName,
      },
      prelead: {
        initialProduct: "death",
        landingPage: landingPage.keyword,
        partner: partnerName,
      },
    };

    await loginActions.register(data);
    await createMutation.mutateAsync({
      uri: "/deaths",
      data: {
        acquisitionPartner: partner,
        affiliateCode: localStorage.getItem("gclid"),
        landingPage: landingPage.keyword,
      },
    });
    await queryClient.invalidateQueries({
      queryKey: queryKeys.collection("/customers"),
    });
    navigate(from.pathname);
  };

  return (
    <Paper elevation={4} sx={[deathCheckinStyle.formPaper, deathCheckinStyle[color]]}>
      <Box id={idPrefix + "-checkin"} sx={deathCheckinStyle.scrollToAnchor} />
      <Typography variant={"h4"} sx={deathCheckinStyle.heading}>
        {checkinHeading}
      </Typography>
      <FormProvider initialValues={initialValues} submitAction={handleSubmit}>
        <FamilyLawCheckinForm isLoading={isLoading} emailErrorText={emailErrorText} setEmailErrorText={setEmailErrorText} />
      </FormProvider>
    </Paper>
  );
}
